<template>
  <div 
    v-if="shouldRender"
    @click="handleClick" 
    @keydown.enter="handleClick"
    @keydown.space.prevent="handleClick"
    :class="['card', componentType, activeColor, { 
      active: activeStatus,
      'has-trend': trendInfo,
      'clickable': componentType === 'chart-filter-button',
      'loaded': isFullyLoaded,
      'images-loaded': imagesLoaded
    }]"
    :tabindex="componentType === 'chart-filter-button' ? '0' : undefined"
    :role="componentType === 'chart-filter-button' ? 'button' : undefined"
    :aria-pressed="componentType === 'chart-filter-button' ? activeStatus : undefined"
    :aria-label="componentType === 'chart-filter-button' ? `${label}: ${text}${subText ? `, ${subText}` : ''}` : undefined"
  >
    <!-- Loading fallback -->
    <div v-if="!isFullyLoaded" class="loading-placeholder">
      <div class="loading-shimmer"></div>
    </div>
    
    <!-- Ripple effect container -->
    <div class="ripple-container" ref="rippleContainer"></div>
    
    <!-- Info KPIs: title + trend on top row, value alone below (avoids badge overlapping value) -->
    <template v-if="isInfoCard">
      <div class="label-container">
        <span class="label-with-hint">
          <span class="label-text" :title="label">{{ label }}</span>
          <button
            v-if="hint"
            type="button"
            class="hint-button"
            @click.stop.prevent="openHint"
            @keydown.stop
            :aria-label="hint"
          >
            <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true">
              <circle cx="8" cy="8" r="6.2" fill="none" stroke="currentColor" stroke-width="1.4"/>
              <circle cx="8" cy="5.1" r="0.8" fill="currentColor"/>
              <path d="M8 7.4v3.6" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/>
            </svg>
          </button>
        </span>
        <span v-if="isAmended && isAmendedPermitted" class="amended-room">*</span>
        <div class="trend-info" v-if="trendInfo">
          <div class="trend-indicator" :class="[trendInfo.upDown, `trend-${trendInfo.color}`]">
            <img
              v-if="trendInfo.color"
              :class="trendInfo.upDown"
              :src="getTrendImageSrc()"
              :alt="`Trend ${trendInfo.upDown}`"
              @load="onTrendImageLoad"
              @error="onImageError"
            />
            <span class="trend-text">{{ trendInfo.text }}</span>
          </div>
        </div>
      </div>

      <div class="icon-text-container">
        <transition name="icon-fade" mode="out-in">
          <img
            v-if="activeIconUrl && activeStatus"
            class="icon-active"
            :src="getIconSrc(activeIconUrl)"
            :alt="`${label} active icon`"
            key="active-icon"
            @load="onIconLoad"
            @error="onImageError"
          />
          <img
            v-else-if="iconUrl"
            class="icon"
            :src="getIconSrc(iconUrl)"
            :alt="`${label} icon`"
            key="default-icon"
            @load="onIconLoad"
            @error="onImageError"
          />
        </transition>
        <span class="main-text">{{ text }}</span>
      </div>

      <div v-if="subText" class="sub-text" :title="subText">{{ subText }}</div>

      <div v-if="trendInfo && trendInfo.noteKey" class="trend-note" :title="$t(trendInfo.noteKey)">
        {{ $t(trendInfo.noteKey) }}
      </div>
    </template>

    <!-- Chart filter / other: keep title + trend on one row -->
    <template v-else>
      <div class="label-container">
        <span class="label-with-hint">
          <span class="label-text" :title="label">{{ label }}</span>
          <button
            v-if="hint"
            type="button"
            class="hint-button"
            @click.stop.prevent="openHint"
            @keydown.stop
            :aria-label="hint"
          >
            <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true">
              <circle cx="8" cy="8" r="6.2" fill="none" stroke="currentColor" stroke-width="1.4"/>
              <circle cx="8" cy="5.1" r="0.8" fill="currentColor"/>
              <path d="M8 7.4v3.6" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/>
            </svg>
          </button>
        </span>
        <span v-if="isAmended && isAmendedPermitted" class="amended-room">*</span>
        <div class="trend-info" v-if="trendInfo">
          <div class="trend-indicator" :class="[trendInfo.upDown, `trend-${trendInfo.color}`]">
            <img
              v-if="trendInfo.color"
              :class="trendInfo.upDown"
              :src="getTrendImageSrc()"
              :alt="`Trend ${trendInfo.upDown}`"
              @load="onTrendImageLoad"
              @error="onImageError"
            />
            <span class="trend-text">{{ trendInfo.text }}</span>
          </div>
        </div>
      </div>

      <div :class="['icon-text-container', spaceIconText ? 'space' : '']">
        <transition name="icon-fade" mode="out-in">
          <img
            v-if="activeIconUrl && activeStatus"
            class="icon-active"
            :src="getIconSrc(activeIconUrl)"
            :alt="`${label} active icon`"
            key="active-icon"
            @load="onIconLoad"
            @error="onImageError"
          />
          <img
            v-else-if="iconUrl"
            class="icon"
            :src="getIconSrc(iconUrl)"
            :alt="`${label} icon`"
            key="default-icon"
            @load="onIconLoad"
            @error="onImageError"
          />
        </transition>
        <span class="main-text">{{ text }}</span>
      </div>

      <div v-if="subText" class="sub-text" :title="subText">{{ subText }}</div>
    </template>
    
    <!-- Micro-interaction highlight -->
    <div class="highlight-overlay"></div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'

export default {
  name: 'BarItem',
  data() {
    return {
      isHovered: false,
      hintPop: null,
      isFocused: false,
      shouldRender: false,
      isFullyLoaded: false,
      imagesLoaded: false,
      loadedImages: new Set(),
      renderTimeout: null,
      loadingTimeout: null,
      totalImages: 0,
      fallbackImages: {
        // Fallback images for when assets fail to load
        default: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjQiIGhlaWdodD0iMjQiIHZpZXdCb3g9IjAgMCAyNCAyNCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjI0IiBoZWlnaHQ9IjI0IiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik0xMiA4VjE2TTggMTJIMTYiIHN0cm9rZT0iIzlDQTNBRiIgc3Ryb2tlLXdpZHRoPSIyIiBzdHJva2UtbGluZWNhcD0icm91bmQiLz4KPC9zdmc+',
        arrow: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAiIGhlaWdodD0iMTAiIHZpZXdCb3g9IjAgMCAxMCAxMCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggZD0iTTUgMkw4IDVMNSA4TDIgNUw1IDJaIiBmaWxsPSJjdXJyZW50Q29sb3IiLz4KPC9zdmc+'
      }
    }
  },
  props: {
    isAmended: {
      type: Number,
      required: false,
    },
    componentType: {
      type: String,
      validator: (prop) => prop === 'chart-filter-button' || prop === 'info',
      required: true,
    },
    text: { type: [String, Number], required: true },
    subText: { type: String, required: false },
    hint: { type: String, required: false },
    spaceIconText: { type: Boolean, default: true },
    label: String,
    activeColor: String,
    activeStatus: Boolean,
    iconUrl: String,
    activeIconUrl: String,
    trendInfo: { type: Object, required: false },
  },
  watch: {
    // Watch for prop changes that might affect image loading
    iconUrl: {
      handler() {
        this.resetImageLoading();
      }
    },
    activeIconUrl: {
      handler() {
        this.resetImageLoading();
      }
    },
    activeStatus: {
      handler() {
        // this.resetImageLoading();
      }
    }
  },
  methods: {
    openHint(event) {
      if (this.hintPop) {
        this.closeHint()
        return
      }
      window.dispatchEvent(new CustomEvent('bar-item-hint-close'))
      const anchor = event.currentTarget.getBoundingClientRect()
      const pop = document.createElement('div')
      pop.setAttribute('role', 'tooltip')
      pop.textContent = this.hint
      Object.assign(pop.style, {
        position: 'fixed',
        zIndex: '10000',
        width: '240px',
        maxWidth: 'calc(100vw - 16px)',
        background: '#ffffff',
        color: '#1f2937',
        border: '1px solid #e5e7eb',
        borderRadius: '8px',
        boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
        padding: '10px 12px',
        fontSize: '13px',
        lineHeight: '1.45',
        textAlign: 'start',
      })
      pop.addEventListener('click', (clickEvent) => clickEvent.stopPropagation())
      document.body.appendChild(pop)
      const width = pop.offsetWidth
      const height = pop.offsetHeight
      const left = Math.max(8, Math.min(anchor.left, window.innerWidth - width - 8))
      let top = anchor.bottom + 6
      if (top + height > window.innerHeight - 8) {
        top = Math.max(8, anchor.top - height - 6)
      }
      pop.style.left = `${left}px`
      pop.style.top = `${top}px`
      this.hintPop = pop
      setTimeout(() => document.addEventListener('click', this.closeHint), 0)
    },

    closeHint() {
      if (this.hintPop) {
        this.hintPop.remove()
        this.hintPop = null
      }
      document.removeEventListener('click', this.closeHint)
    },

    handleClick(event) {
      // Add ripple effect for interactive elements
      if (this.componentType === 'chart-filter-button') {
        this.createRipple(event);
      }
      
      this.$emit('click', { isActive: this.activeStatus });
    },
    
    createRipple(event) {
      const container = this.$refs.rippleContainer;
      if (!container) return;
      
      const rect = this.$el.getBoundingClientRect();
      const x = event.clientX - rect.left;
      const y = event.clientY - rect.top;
      
      const ripple = document.createElement('div');
      ripple.className = 'ripple';
      ripple.style.left = `${x}px`;
      ripple.style.top = `${y}px`;
      
      container.appendChild(ripple);
      
      // Remove ripple after animation
      setTimeout(() => {
        if (container.contains(ripple)) {
          container.removeChild(ripple);
        }
      }, 600);
    },

    getIconSrc(iconPath) {
      if (!iconPath) return this.fallbackImages.default;
      
      try {
        // Try to require the image
        return require(`@/${iconPath}`);
      } catch (error) {
        console.warn(`Failed to load icon: ${iconPath}`, error);
        return this.fallbackImages.default;
      }
    },

    getTrendImageSrc() {
      if (!this.trendInfo || !this.trendInfo.color) {
        return this.fallbackImages.arrow;
      }

      try {
        const colorPath = this.activeStatus ? 'white' : this.trendInfo.color;
        return require(`@/assets/performance/${colorPath}_arrow.svg`);
      } catch (error) {
        console.warn(`Failed to load trend arrow: ${this.trendInfo.color}`, error);
        return this.fallbackImages.arrow;
      }
    },

    onIconLoad() {
      this.loadedImages.add('icon');
      this.checkAllImagesLoaded();
    },

    onTrendImageLoad() {
      this.loadedImages.add('trend');
      this.checkAllImagesLoaded();
    },

    onImageError(event) {
      console.warn('Image failed to load:', event.target.src);
      // Still count as "loaded" to prevent infinite waiting
      this.loadedImages.add('error');
      this.checkAllImagesLoaded();
    },

    checkAllImagesLoaded() {
      // Calculate expected number of images
      let expectedImages = 0;
      
      if (this.iconUrl || this.activeIconUrl) expectedImages++;
      if (this.trendInfo && this.trendInfo.color) expectedImages++;
      
      // If no images expected or all images loaded
      if (expectedImages === 0 || this.loadedImages.size >= expectedImages) {
        this.imagesLoaded = true;
        this.finalizeLoading();
      }
    },

    resetImageLoading() {
      this.loadedImages.clear();
      this.imagesLoaded = false;
      this.isFullyLoaded = false;
      
      // Re-check images after a short delay
      this.$nextTick(() => {
        setTimeout(() => {
          this.checkAllImagesLoaded();
        }, 50);
      });
    },

    finalizeLoading() {
      if (!this.isFullyLoaded) {
        this.isFullyLoaded = true;
        
        // Clear any pending timeouts
        if (this.loadingTimeout) {
          clearTimeout(this.loadingTimeout);
          this.loadingTimeout = null;
        }
        
        // Emit that component is ready
        this.$emit('component-ready');
      }
    },

    initializeComponent() {
      // Set up initial render
      this.shouldRender = true;
      
      // Set up loading timeout as fallback
      this.loadingTimeout = setTimeout(() => {
        if (!this.isFullyLoaded) {
          console.warn('BarItem: Forcing load after timeout for', this.label);
          this.imagesLoaded = true;
          this.finalizeLoading();
        }
      }, 2000); // 2 second timeout
      
      // Check if we have any images to load
      this.$nextTick(() => {
        this.checkAllImagesLoaded();
      });
    }
  },
  
  mounted() {
    // Initialize component after a minimal delay to ensure DOM is ready
    this.renderTimeout = setTimeout(() => {
      this.initializeComponent();
    }, 10);
    
    this.$emit('mounted');
    window.addEventListener('bar-item-hint-close', this.closeHint)
  },

  beforeDestroy() {
    this.closeHint()
    window.removeEventListener('bar-item-hint-close', this.closeHint)
  },

  beforeUnmount() {
    // Clean up timeouts
    if (this.renderTimeout) {
      clearTimeout(this.renderTimeout);
    }
    if (this.loadingTimeout) {
      clearTimeout(this.loadingTimeout);
    }
  },
  
  computed: {
    ...mapGetters(['loggedUser']),
    isInfoCard() {
      return this.componentType === 'info';
    },
    isAmendedPermitted() {
      // Add defensive check for store data
      if (!this.loggedUser || typeof this.loggedUser !== 'object') {
        return false;
      }

      const { level } = this.loggedUser;
      return level === 'buzzz';
    },
  },
}
</script>

<style lang="scss" scoped>
@use "@/styles/mixins" as *;

.loading-placeholder {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(248, 250, 252, 0.95);
  border-radius: inherit;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;

  .loading-shimmer {
    width: 60%;
    height: 20px;
    background: linear-gradient(
      90deg,
      rgba(226, 232, 240, 0.8) 0%,
      rgba(203, 213, 225, 0.9) 50%,
      rgba(226, 232, 240, 0.8) 100%
    );
    border-radius: 4px;
    animation: shimmer 1.5s infinite;
  }
}

@keyframes shimmer {
  0% { transform: translateX(-100%); }
  100% { transform: translateX(100%); }
}

.amended-room {
  color: #3b82f6;
  display: flex;
  align-items: center;
  margin-left: 8px;
  font-weight: 600;
  transition: all 0.3s ease;
  
  &:hover {
    transform: scale(1.1);
    color: #1d4ed8;
  }
  
  // Mobile adjustments
  @media (max-width: 768px) {
    margin-left: 4px;
    font-size: 0.9rem;
  }
}

.card {
  position: relative;
  padding: 18px;
  min-width: 9.5vw;
  max-width: 11vw;
  max-height: 140px;
  background: rgba(255, 255, 255, 0.98);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 16px;
  box-shadow: 
    0 8px 32px rgba(0, 0, 0, 0.06),
    0 2px 8px rgba(0, 0, 0, 0.04),
    inset 0 1px 0 rgba(255, 255, 255, 0.8);
  transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  backdrop-filter: blur(20px);
  opacity: 0;
  overflow: hidden;
  isolation: isolate;

  // Only animate when fully loaded
  &.loaded {
    animation: fadeInUp 0.6s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards;
  }

  // Immediate show for images loaded
  &.images-loaded {
    opacity: 1;
  }

  // Ripple effect container
  .ripple-container {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    pointer-events: none;
    z-index: 1;

    .ripple {
      position: absolute;
      width: 40px;
      height: 40px;
      border-radius: 50%;
      background: rgba(255, 255, 255, 0.6);
      transform: translate(-50%, -50%) scale(0);
      animation: rippleEffect 0.6s cubic-bezier(0.175, 0.885, 0.32, 1.275);
      pointer-events: none;
    }
  }

  // Micro-interaction highlight with gradient mesh effect
  .highlight-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: 
      radial-gradient(circle at 30% 20%, rgba(99, 102, 241, 0.1), transparent 50%),
      radial-gradient(circle at 70% 80%, rgba(168, 85, 247, 0.08), transparent 50%),
      linear-gradient(135deg, rgba(255, 255, 255, 0.15), transparent);
    opacity: 0;
    transition: opacity 0.4s ease;
    pointer-events: none;
    border-radius: inherit;
    z-index: 2;
  }

  &:hover {
    transform: translateY(-8px) scale(1.02);
    box-shadow: 
      0 20px 60px rgba(0, 0, 0, 0.12),
      0 8px 24px rgba(0, 0, 0, 0.08),
      inset 0 1px 0 rgba(255, 255, 255, 0.9);
  }

  &.clickable {
    cursor: pointer;
    
    &:active {
      transform: translateY(-4px) scale(0.98);
    }
  }

  .label-with-hint {
    display: inline-flex;
    align-items: flex-start;
    gap: 4px;
    min-width: 0;
    max-width: 100%;
  }

  .hint-button {
    flex: 0 0 auto;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 16px;
    height: 16px;
    margin-top: 1px;
    padding: 0;
    border: 0;
    background: transparent;
    color: #6b7280;
    cursor: pointer;
    position: relative;
    z-index: 4;

    &:hover,
    &:focus {
      color: #111827;
    }
  }

  .label-container {
    position: relative;
    z-index: 3;
    margin-bottom: 16px;
    height: 22px;
    font-size: 0.875rem;
    display: flex;
    justify-content: space-between;
    align-items: center;

    .label-text {
      display: block;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      flex-shrink: 1;
      font-weight: 500;
      letter-spacing: 0.025em;
    }
  }

  .trend-info {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    margin-inline-start: 6px;
    flex-shrink: 0;

    .trend-indicator {
      display: flex;
      align-items: center;
      gap: 2px;
      padding: 3px 6px;
      border-radius: 10px;
      transition: all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
      backdrop-filter: blur(10px);
      font-size: 0.7rem;

      &.trend-green, &.up {
        background: rgba(34, 197, 94, 0.12);
        color: #047857;
        border: 1px solid rgba(34, 197, 94, 0.25);
        box-shadow: 0 2px 8px rgba(34, 197, 94, 0.1);
      }

      &.trend-red, &.down {
        background: rgba(239, 68, 68, 0.12);
        color: #b91c1c;
        border: 1px solid rgba(239, 68, 68, 0.25);
        box-shadow: 0 2px 8px rgba(239, 68, 68, 0.1);
      }

      &.trend-blue {
        background: rgba(59, 130, 246, 0.12);
        color: #1d4ed8;
        border: 1px solid rgba(59, 130, 246, 0.25);
        box-shadow: 0 2px 8px rgba(59, 130, 246, 0.1);
      }

      &.trend-orange {
        background: rgba(249, 115, 22, 0.12);
        color: #c2410c;
        border: 1px solid rgba(249, 115, 22, 0.25);
        box-shadow: 0 2px 8px rgba(249, 115, 22, 0.1);
      }

      &.trend-purple {
        background: rgba(168, 85, 247, 0.12);
        color: #6d28d9;
        border: 1px solid rgba(168, 85, 247, 0.25);
        box-shadow: 0 2px 8px rgba(168, 85, 247, 0.1);
      }

      &:not([class*="trend-"]) {
        background: rgba(107, 114, 128, 0.12);
        color: #4b5563;
        border: 1px solid rgba(107, 114, 128, 0.25);
      }

      img {
        height: 10px;
        width: 10px;
        transition: transform 0.3s ease;
        filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.1));

        &.down {
          transform: rotate(180deg);
        }

        &.up {
          transform: rotate(0deg);
        }
      }

      .trend-text {
        font-size: 0.65rem;
        font-weight: 600;
        letter-spacing: 0.025em;
        white-space: nowrap;
      }

      &:hover {
        transform: translateY(-1px) scale(1.05);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
      }
    }
  }

  .icon-text-container {
    position: relative;
    z-index: 3;
    font-size: 1.3rem;
    font-weight: 600;
    display: flex;
    align-items: center;
    gap: 10px;

    .icon, .icon-active {
      transition: all 0.3s ease;
      filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1));
      
      &:hover {
        transform: scale(1.05);
      }
    }

    .main-text {
      color: #1f2937;
      letter-spacing: -0.025em;
      flex: 1;
    }

    &.space {
      justify-content: space-between;
    }
  }

  .sub-text {
    position: relative;
    z-index: 3;
    font-size: 0.75rem;
    font-weight: 500;
    color: #64748b;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .trend-note {
    position: relative;
    z-index: 3;
    margin-top: 6px;
    font-size: 0.65rem;
    font-weight: 500;
    line-height: 1.25;
    color: #64748b;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    overflow: hidden;
  }

  &.info {
    // No explicit width here: when the parent is the .info-bar grid, Grid's
    // default `stretch` fills the column automatically, guaranteeing every
    // card the exact same width no matter how much content it holds. When
    // the parent is a plain flex-wrap container (e.g. PDF export), the card
    // simply sizes to its content instead, which is the desired fallback.
    box-sizing: border-box;
    min-width: 0;
    max-width: none;
    max-height: none !important;
    overflow: visible;
    padding: 12px 14px 30px;
    border: 1px solid rgba(229, 231, 235, 0.6);
    background: rgba(249, 250, 251, 0.95);
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    gap: 6px;

    // The share line sits in this bottom band, so the count stays on the
    // same row as the counts on the cards that have no second line.
    .sub-text {
      position: absolute;
      inset-inline: 14px;
      bottom: 8px;
      margin: 0;
    }

    .label-container {
      height: auto;
      min-height: 0;
      margin-bottom: 0;
      align-items: flex-start;
      gap: 6px;

      .label-text {
        color: #4b5563;
        white-space: normal;
        overflow: hidden;
        text-overflow: unset;
        display: -webkit-box;
        -webkit-box-orient: vertical;
        -webkit-line-clamp: 2;
        line-clamp: 2;
        line-height: 1.25;
        // Reserve space for 2 lines even when the label only needs 1, so the
        // value row below starts at the same height on every card - some
        // labels wrap (e.g. "Response time") and some don't (e.g. "Visits"),
        // and without this the value rows would drift out of alignment.
        min-height: 2.5em;
        flex: 1 1 auto;
        min-width: 0;
        max-width: none;
      }

      .trend-info {
        margin-inline-start: 0;
        align-self: flex-start;
        flex: 0 0 auto;

        .trend-indicator {
          padding: 1px 4px;
          gap: 1px;

          img {
            height: 8px;
            width: 8px;
          }

          .trend-text {
            font-size: 0.6rem;
          }

          background: rgba(132, 133, 147, 0.08);
          color: #6b7280;
          border: 1px solid rgba(132, 133, 147, 0.15);

          &.trend-green,
          &.up {
            background: rgba(34, 197, 94, 0.08);
            color: #16a34a;
          }

          &.trend-red,
          &.down {
            background: rgba(239, 68, 68, 0.08);
            color: #dc2626;
          }
        }
      }
    }

    .icon-text-container {
      display: flex;
      flex-direction: row;
      flex-wrap: nowrap;
      align-items: center;
      justify-content: flex-start;
      gap: 6px;
      min-height: 0;
      height: auto;
      width: 100%;

      .main-text {
        color: #374151;
        flex: 0 1 auto;
        min-width: 0;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      img.icon,
      img.icon-active {
        margin-inline-end: 0;
        border-radius: 6px;
        max-height: 22px;
        max-width: 22px;
        flex-shrink: 0;
      }
    }
  }

  &.chart-filter-button {
    cursor: pointer;
    border: 1px solid rgba(229, 231, 235, 0.6);
    border-inline-end: unset;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    background: rgba(255, 255, 255, 0.9);

    .label-container {
      color: #6b7280;
      font-size: 0.8rem;
      font-weight: 500;
    }

    &.last {
      border-inline-end: 1px solid rgba(229, 231, 235, 0.6);
    }

    &.active {
      border-radius: 16px;
      border: 2px solid rgba(255, 255, 255, 0.4);
      color: #ffffff;
      transform: translateY(-6px) scale(1.03);
      box-shadow: 
        0 25px 50px rgba(0, 0, 0, 0.25),
        0 10px 20px rgba(0, 0, 0, 0.15),
        inset 0 1px 0 rgba(255, 255, 255, 0.3);

      .label-container {
        color: rgba(255, 255, 255, 0.95);
      }

      .icon-text-container .main-text {
        color: #ffffff;
        text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
      }

      .hint-button {
        color: rgba(255, 255, 255, 0.95);
      }

      .sub-text {
        color: rgba(255, 255, 255, 0.9);
      }

      .trend-info .trend-indicator {
        background: rgba(255, 255, 255, 0.25);
        color: rgba(255, 255, 255, 0.95);
        border: 1px solid rgba(255, 255, 255, 0.4);
        backdrop-filter: blur(15px);
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);

        &:hover {
          background: rgba(255, 255, 255, 0.35);
          transform: translateY(-1px) scale(1.05);
        }
      }

      // Using the original SCSS variables (these should be imported from your mixins)
      &.bouquet {
        background-color: $bouquet;
      }

      &.persian-red {
        background-color: $persian-red;
      }

      &.yellow {
        background-color: $yellow;
      }

      &.flame-pea {
        background-color: $flame-pea;
      }

      &.jaffa {
        background-color: $jaffa;
      }

      &.sea-green {
        background-color: $sea-green;
      }

      &.saffron {
        background-color: $saffron;
      }

      &.goblin-green {
        background-color: $goblin-green;
      }

      &.wedgewood-blue {
        background-color: $wedgewood-blue;
      }
      
      &.deep-sky-blue {
        background-color: $deep-sky-blue;
      }

      &.dark-cyan {
        background-color: $dark-cyan;
      }

      &:hover {
        transform: translateY(-8px) scale(1.05);
        box-shadow: 
          0 30px 60px rgba(0, 0, 0, 0.3),
          0 12px 24px rgba(0, 0, 0, 0.2),
          inset 0 1px 0 rgba(255, 255, 255, 0.4);
      }
    }

    .icon-text-container {
      display: flex;
      justify-content: space-between;
      letter-spacing: -0.2px;
      align-items: center;
      gap: 8px;

      img {
        border-radius: 6px;
        max-height: 22px;
        max-width: 22px;
      }
    }
  }

  // Enhanced animations
  @keyframes fadeInUp {
    0% {
      opacity: 0;
      transform: translateY(30px) scale(0.9) rotateX(10deg);
      filter: blur(10px);
    }
    60% {
      opacity: 0.8;
      transform: translateY(8px) scale(0.98) rotateX(2deg);
      filter: blur(2px);
    }
    100% {
      opacity: 1;
      transform: translateY(0) scale(1) rotateX(0deg);
      filter: blur(0px);
    }
  }

  @keyframes rippleEffect {
    0% {
      transform: translate(-50%, -50%) scale(0);
      opacity: 1;
    }
    100% {
      transform: translate(-50%, -50%) scale(4);
      opacity: 0;
    }
  }

  // ========================================
  // RESPONSIVE BREAKPOINTS
  // ========================================

  // Extra large screens (1440px and up)
  @media (min-width: 1440px) {
    min-width: 10vw;
    max-width: 12vw;
    padding: 20px;

    &.info {
      max-width: none;
      max-height: none;
    }
    
    .icon-text-container {
      font-size: 1.4rem;
      gap: 12px;
    }
    
    .label-container {
      font-size: 0.95rem;
      margin-bottom: 18px;
    }

    &.info .label-container {
      margin-bottom: 10px;
    }
  }

  // Large screens (1200px to 1439px)
  @media (min-width: 1200px) and (max-width: 1439px) {
    min-width: 9vw;
    max-width: 11vw;
    
    &.info {
      max-width: none;
      max-height: none;
    }
  }

  // Desktop (992px to 1199px)
  @media (min-width: 992px) and (max-width: 1199px) {
    min-width: 11vw;
    max-width: 13vw;
    
    &.info {
      max-width: none;
      max-height: none;
    }
    
    .icon-text-container {
      font-size: 1.2rem;
    }
  }

  // Tablet landscape (768px to 991px)
  @media (min-width: 768px) and (max-width: 991px) {
    min-width: 140px;
    max-width: 180px;
    padding: 16px;
    max-height: 120px;
    
    &.info {
      max-width: none;
      max-height: none;
    }
    
    .icon-text-container {
      font-size: 1.1rem;
      gap: 8px;
      
      img {
        max-height: 20px;
        max-width: 20px;
      }
    }
    
    .label-container {
      font-size: 0.8rem;
      margin-bottom: 12px;
      
      .trend-info .trend-indicator {
        font-size: 0.65rem;
        padding: 2px 4px;
        
        img {
          height: 8px;
          width: 8px;
        }
      }
    }
    
    &:hover {
      transform: translateY(-6px) scale(1.015);
    }
  }

  // Tablet portrait and small tablets (576px to 767px)
  @media (min-width: 576px) and (max-width: 767px) {
    min-width: 120px;
    max-width: 160px;
    padding: 14px;
    max-height: 110px;
    
    &.info {
      max-width: none;
      max-height: none;
    }
    
    .icon-text-container {
      font-size: 1rem;
      gap: 6px;
      
      img {
        max-height: 18px;
        max-width: 18px;
      }
    }
    
    .label-container {
      font-size: 0.75rem;
      margin-bottom: 10px;
      height: auto;
      min-height: 18px;
      
      .trend-info {
        margin-inline-start: 4px;
        
        .trend-indicator {
          font-size: 0.6rem;
          padding: 2px 4px;
          gap: 1px;
          
          img {
            height: 7px;
            width: 7px;
          }
          
          .trend-text {
            font-size: 0.6rem;
          }
        }
      }
    }
    
    &:hover {
      transform: translateY(-4px) scale(1.01);
    }
    
    &.chart-filter-button.active {
      transform: translateY(-4px) scale(1.02);
      
      &:hover {
        transform: translateY(-6px) scale(1.03);
      }
    }
  }

  // Mobile landscape and large mobile (480px to 575px)
  @media (min-width: 480px) and (max-width: 575px) {
    min-width: 100px;
    max-width: 140px;
    padding: 12px;
    max-height: 100px;
    border-radius: 12px;
    
    &.info {
      max-width: none;
      max-height: none;
    }
    
    .icon-text-container {
      font-size: 0.95rem;
      gap: 6px;
      
      img {
        max-height: 16px;
        max-width: 16px;
      }
    }
    
    .label-container {
      font-size: 0.7rem;
      margin-bottom: 8px;
      
      .trend-info .trend-indicator {
        font-size: 0.55rem;
        padding: 1px 3px;
        
        img {
          height: 6px;
          width: 6px;
        }
        
        .trend-text {
          font-size: 0.55rem;
        }
      }
    }
    
    &:hover {
      transform: translateY(-3px) scale(1.005);
    }
  }

  // Mobile portrait (320px to 479px)
  @media (max-width: 479px) {
    min-width: 85px;
    max-width: 120px;
    padding: 10px;
    max-height: 90px;
    border-radius: 10px;
    
    &.info {
      max-width: none;
      max-height: none;
    }
    
    .icon-text-container {
      font-size: 0.85rem;
      gap: 4px;
      
      img {
        max-height: 14px;
        max-width: 14px;
      }
    }
    
    .label-container {
      font-size: 0.65rem;
      margin-bottom: 6px;
      
      .trend-info {
        margin-inline-start: 2px;
        
        .trend-indicator {
          font-size: 0.5rem;
          padding: 1px 2px;
          
          img {
            height: 5px;
            width: 5px;
          }
          
          .trend-text {
            font-size: 0.5rem;
          }
        }
      }
    }
    
    // Reduce hover effects on very small screens
    &:hover {
      transform: translateY(-2px);
    }
    
    &.chart-filter-button.active {
      transform: translateY(-2px) scale(1.01);
      
      &:hover {
        transform: translateY(-3px) scale(1.015);
      }
    }
  }

  // Extra small screens (below 320px)
  @media (max-width: 319px) {
    min-width: 70px;
    max-width: 100px;
    padding: 8px;
    max-height: 80px;
    border-radius: 8px;
    
    &.info {
      max-width: none;
      max-height: none !important;
    }
    
    .icon-text-container {
      font-size: 0.75rem;
      gap: 3px;
      
      img {
        max-height: 12px;
        max-width: 12px;
      }
    }
    
    .label-container {
      font-size: 0.6rem;
      margin-bottom: 4px;
      
      .trend-info .trend-indicator {
        font-size: 0.45rem;
        padding: 1px;
        
        img {
          height: 4px;
          width: 4px;
        }
        
        .trend-text {
          font-size: 0.45rem;
        }
      }
    }
    
    // Minimal hover effects
    &:hover {
      transform: translateY(-1px);
    }
  }

  // Print styles
  @media print {
    &.info {
      width: 16vw;
      margin: 0 4px;
      border-radius: 16px !important;
      background: #ffffff 0% 0% no-repeat padding-box;
      box-shadow: 1px 2px 4px #00000029;
      border: 1px solid #e9e9e9;
    }
    
    &.chart-filter-button {
      width: 140px;
      height: 95px;
    }
  }
}

// Icon transition animations
.icon-fade-enter-active,
.icon-fade-leave-active {
  transition: all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
}

.icon-fade-enter-from {
  opacity: 0;
  transform: scale(0.8) rotate(5deg);
}

.icon-fade-leave-to {
  opacity: 0;
  transform: scale(0.8) rotate(-5deg);
}

// Accessibility improvements
@media (prefers-reduced-motion: reduce) {
  .card {
    animation: none;
    transition: box-shadow 0.2s ease, background-color 0.2s ease;
    
    &:hover {
      transform: none;
    }
    
    .ripple {
      animation: none;
    }
    
    .highlight-overlay {
      transition: opacity 0.2s ease;
    }

    .loading-shimmer {
      animation: none;
    }
  }
}

// High contrast mode
@media (prefers-contrast: high) {
  .card {
    border: 2px solid #000;
    background: #fff;
    
    &.active {
      border: 2px solid #fff;
    }
    
    .trend-info .trend-indicator {
      border-width: 2px;
    }

    .loading-placeholder {
      background: #f0f0f0;
      border: 1px solid #ccc;
    }
  }
}

// ========================================
// CONTAINER GRID RESPONSIVE UTILITIES
// ========================================

// Helper classes for responsive card containers
.card-container {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  
  @media (max-width: 768px) {
    gap: 8px;
  }
  
  @media (max-width: 479px) {
    gap: 6px;
  }
}

.card-grid {
  display: grid;
  gap: 12px;
  
  // Desktop grid (4-5 columns)
  @media (min-width: 1200px) {
    grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  }
  
  // Tablet grid (3-4 columns)
  @media (min-width: 768px) and (max-width: 1199px) {
    grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
    gap: 10px;
  }
  
  // Mobile landscape (2-3 columns)
  @media (min-width: 480px) and (max-width: 767px) {
    grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
    gap: 8px;
  }
  
  // Mobile portrait (2 columns)
  @media (max-width: 479px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 6px;
  }
}

// ========================================
// ORIENTATION SPECIFIC STYLES
// ========================================

@media (orientation: landscape) and (max-height: 500px) {
  .card {
    padding: 8px 12px;
    max-height: 70px;
    
    .label-container {
      margin-bottom: 4px;
      font-size: 0.7rem;
    }
    
    .icon-text-container {
      font-size: 0.9rem;
      
      img {
        max-height: 14px;
        max-width: 14px;
      }
    }
  }
}

// ========================================
// TOUCH DEVICE OPTIMIZATIONS
// ========================================

@media (hover: none) and (pointer: coarse) {
  .card {
    // Larger touch targets for mobile
    min-height: 80px;
    
    &.clickable {
      // Enhanced touch feedback
      &:active {
        transform: scale(0.95);
        transition: transform 0.1s ease;
      }
    }
    
    // Remove hover effects on touch devices
    &:hover {
      transform: none;
      box-shadow: 
        0 8px 32px rgba(0, 0, 0, 0.06),
        0 2px 8px rgba(0, 0, 0, 0.04),
        inset 0 1px 0 rgba(255, 255, 255, 0.8);
    }
    
    .highlight-overlay {
      display: none;
    }
    
    .trend-info .trend-indicator {
      &:hover {
        transform: none;
      }
    }
  }
}

// ========================================
// PERFORMANCE OPTIMIZATIONS
// ========================================

// Reduce animations on lower-end devices
@media (prefers-reduced-motion: reduce) {
  .card {
    .ripple-container .ripple {
      animation: none;
    }
    
    .icon-fade-enter-active,
    .icon-fade-leave-active {
      transition: opacity 0.2s ease;
    }
    
    .trend-info .trend-indicator {
      transition: background-color 0.2s ease;
    }
  }
}

// GPU acceleration for smoother animations
@media (min-width: 768px) {
  .card {
    will-change: transform, box-shadow;
    
    &:hover {
      will-change: auto;
    }
  }
}

// ========================================
// UTILITY CLASSES FOR RESPONSIVE BEHAVIOR
// ========================================

// Hide/show cards at different breakpoints
.card-hide-mobile {
  @media (max-width: 767px) {
    display: none;
  }
}

.card-hide-tablet {
  @media (min-width: 768px) and (max-width: 1199px) {
    display: none;
  }
}

.card-hide-desktop {
  @media (min-width: 1200px) {
    display: none;
  }
}

// Mobile-first show utilities
.card-show-mobile {
  display: none;
  
  @media (max-width: 767px) {
    display: block;
  }
}

.card-show-tablet {
  display: none;
  
  @media (min-width: 768px) and (max-width: 1199px) {
    display: block;
  }
}

.card-show-desktop {
  display: none;
  
  }
</style>